/* Verbatim port of renderSearch from service/review/index.html (~1580-1663)
 * — §1 Search results (the IMPACT-view shell: axis bar, canvas mount,
 * key-domains table, "Has this moved?" mount). The IMPACT timeline
 * subsystem itself (wireImpControls/loadTimeline/renderImpCanvas/
 * renderKeyDomains) is views/timeline.ts; the mention-velocity subsystem
 * (renderTrend/mvLoad/…) is views/trend.ts; the result popover is
 * ui/popover.ts. All four files import from each other (renderSearch wires
 * up the timeline + trend subsystems; the timeline's axis/term buttons
 * call back into renderSearch; the popover reads the current term) —
 * that's a real circular import (search.ts <-> timeline.ts <-> popover.ts),
 * which ES modules support fine as long as the shared bindings are only
 * read inside function bodies (never at module-eval time), which is the
 * case here.
 *
 * PARAMETERIZED (rule a): reads the global `STATE` — takes `state:
 * AppPayloadState` as an explicit first parameter.
 *
 * DEVIATION: `SERP_AXIS`/`SERP_TERM` (originally ~1569-1570, plain `let`
 * globals mutated directly by views/timeline.ts's wireImpControls and read
 * by ui/popover.ts's openResultPopover) stay module-local to this file per
 * the brief, but ES import bindings are read-only from the importing
 * module — timeline.ts cannot do `SERP_TERM = x` on an imported binding.
 * Exported getSerpAxis/getSerpTerm/setSerpAxis/setSerpTerm accessors
 * replace direct reads/writes in every OTHER module (this file still uses
 * the bare variables internally). Same shape as state/ui.ts's treatment of
 * the analogous ACT_STATUS/FLAGGED bare-global-plus-load/save globals.
 *
 * ── F3c (wave C) lit-html conversion — per the wiki.ts conventions block ──
 * `tip()` -> `tipT()`. The axisbar's buttons/selects (#modeSeg/#zoomSeg/
 * #axisSeg/#termSeg/#tlLoc/#tlStart/#tlEnd/#tlHighlight) keep their exact
 * markup (ids, data-* attributes, the same `class="${cond?'on':''}"`
 * pattern lit binds natively) — this template still owns rendering THEM,
 * it just doesn't wire their behavior itself: wireImpControls (timeline.ts)
 * does that immediately after render(), same as the original's call order.
 * `.map(t => \`...\`).join('')` -> array of html`` (empty-separator case,
 * convention #6).
 */
import { html, nothing, render } from 'lit';
import { ownershipByTermLoc, searchHeadline } from '../derive/search.ts';
import { AXES } from '../derive/axes.ts';
import { fixMojibake, listJoin } from '../lib/format.ts';
import { tipT } from '../lib/templates.ts';
import { TL_STATE, refreshImpCanvas, renderKeyDomains, wireImpControls } from './timeline.ts';
import { renderTrend } from './trend.ts';
import { renderSmallMultiples } from './smallMultiples.ts';
import { planePillClass, planeProvenanceLine, planeState } from '../derive/planes.ts';
import { setSerpRoot } from './mountRoot.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export type SerpAxisKey = 'sentiment' | 'ownership' | 'authority';

// default to Ownership: it's the axis IMPACT measured for EVERY city, so the
// grid opens full of data instead of "unrated" — sentiment shines on the ★ page
let SERP_AXIS: SerpAxisKey = 'ownership';
let SERP_TERM: string | null = null;

export function getSerpAxis(): SerpAxisKey { return SERP_AXIS; }
export function setSerpAxis(a: SerpAxisKey): void { SERP_AXIS = a; }
export function getSerpTerm(): string | null { return SERP_TERM; }
export function setSerpTerm(t: string | null): void { SERP_TERM = t; }

/* `target` (Package 4): the mount to render the IMPACT §1 into. Defaults to the
 * legacy #serpBody (or the current serpRoot when a control handler re-renders),
 * so the legacy section and the deep Search page each render into their own
 * mount with no node movement. setSerpRoot() scopes the sub-renderers below. */
export function renderSearch(state: AppPayloadState, target?: HTMLElement): void {
  const el = target ?? document.getElementById('serpBody')!;
  setSerpRoot(el);
  const rows = ownershipByTermLoc(state);
  const sh = searchHeadline(state);
  const s = state.serp;
  if (!rows.length && !s){
    // Honest empty-state (H1): render the IMPACT plane's stamped provenance so
    // a `skipped` (deliberate scope) plane reads neutral and an
    // `unavailable`/`error` (dark) plane reads amber-with-reason — never the
    // old "joins in at the cross-referencing stage" coming-soon promise. A
    // legacy run (no planeStatus) falls back to a neutral, promise-free line.
    const prov = planeProvenanceLine('impact', planeState(state.planeStatus, 'impact'));
    render(
      prov
        ? html`<div class="why"><span class="${planePillClass(prov.tone)}">${prov.text}</span></div>`
        : html`<div class="why">No IMPACT search data in this run.</div>`,
      el,
    );
    return;
  }
  const terms = [...new Set(rows.map(r => r.term))];
  // DESIGN-FIDELITY P0-2: default to a term that actually has a full SERP
  // CAPTURE, so the IMPACT canvas renders readable ranked result rows where the
  // data exists — not the honest-but-skeletal color-column overview a
  // counts-only term falls back to. The full captures often live under one query
  // variant ("ElAl") that is NOT the first tracked term ("El Al"), so blindly
  // defaulting to terms[0] hid the one city with real per-result data. Prefer a
  // captured term; fall back to the first tracked term, then the serp's own query.
  const capturedTerms = new Set(
    [state.serp, ...(state.serps || [])]
      .filter((c): c is NonNullable<typeof c> => !!c)
      .map((c) => fixMojibake(c.query)),
  );
  if (!SERP_TERM || !terms.includes(SERP_TERM)) {
    SERP_TERM = terms.find((t) => capturedTerms.has(t)) || terms[0] || (s && s.query) || null;
  }

  // both ternary branches below are plain text (no markup either side) —
  // convention #7 leaves those as bare strings, not html``/nothing.
  // rows.length > 0 iff best/worst non-null (see derive/search.ts) — same runs render
  const finding = sh && sh.best && sh.worst
    ? html`${state.brand} owns <b>${sh.best.owned} of ${sh.best.slots}</b> page-1 results in ${listJoin(sh.bestLocs)} but only <b>${sh.worst.owned} of ${sh.worst.slots}</b> in ${listJoin(sh.worstLocs)}${sh.negLocs.length ? ` — and searchers in ${listJoin(sh.negLocs)} see an Undesirable result on page one` : ''}.`
    : html`A single branded search was captured this run${s ? ` (“${s.query}”, ${s.location})` : ''}.`;

  // D9 partial-content clause: when the IMPACT plane ERRORED but still folded
  // some data (so we're on the content path, not the empty state), show the
  // amber provenance line ABOVE the content WITHOUT suppressing it — the fold
  // may be partial. Only `error` surfaces here; `ok`/`reused` need no banner and
  // `skipped`/`unavailable` produce no content (handled by the empty state).
  const impactPs = planeState(state.planeStatus, 'impact');
  const partialErr = impactPs && impactPs.status === 'error'
    ? planeProvenanceLine('impact', impactPs) : null;

  render(html`
    ${partialErr ? html`<div class="why"><span class="${planePillClass(partialErr.tone)}">${partialErr.text}</span></div>` : nothing}
    <div class="finding">${finding}</div>
    <div class="why"><b>Why this matters:</b> page one of a branded Google search is the client's shop window — most people never scroll past it. The firm reads it on three axes: ${tipT('Desirable','Sentiment (Desirable / Undesirable / Neutral)')}, ${tipT('Owned/Not Owned/Not Me','Ownership (Owned / Not Owned / Not Me)')}, and ${tipT('authority type','Authority type')}. Location matters: Google composes a different page one in every city, so ownership is always reported per location.</div>

    <div class="card" style="margin-bottom:16px">
      <h3>Search results — the IMPACT view</h3>
      <div class="sub">the branded page one as IMPACT cards: compare cities side-by-side or follow one city over time; zoom between full results and the color-column overview</div>
      <div class="axisbar" id="tlControls">
        <span class="seg" id="modeSeg">
          <button data-m="cities" class="${TL_STATE.mode==='cities'?'on':''}">Across cities</button>
          <button data-m="time" class="${TL_STATE.mode!=='cities'?'on':''}">Over time</button>
        </span>
        ${terms.length > 1 ? html`<span class="lbl">Term</span><span class="seg" id="termSeg">${terms.map(t => html`<button data-t="${t}" class="${t===SERP_TERM?'on':''}">${t}</button>`)}</span>` : nothing}
        <span class="tl-time-only"><span class="lbl">City</span><select id="tlLoc" class="tlsel" aria-label="City for the over-time timeline"></select></span>
        <span class="tl-time-only"><span class="lbl">From</span><input type="date" id="tlStart" class="tlsel" aria-label="Timeline window start date"></span>
        <span class="tl-time-only"><span class="lbl">To</span><input type="date" id="tlEnd" class="tlsel" aria-label="Timeline window end date"></span>
        <span class="lbl">Zoom</span><span class="seg" id="zoomSeg">
          <button data-z="regular" class="${TL_STATE.zoom==='regular'?'on':''}">Regular</button>
          <button data-z="medium" class="${TL_STATE.zoom==='medium'?'on':''}">Medium</button>
          <button data-z="small" class="${TL_STATE.zoom==='small'?'on':''}">Small</button>
        </span>
        <span class="lbl">Color by</span>
        <span class="seg" id="axisSeg">${Object.entries(AXES).map(([k,ax]) => html`<button data-a="${k}" class="${k===SERP_AXIS?'on':''}">${ax.label}</button>`)}</span>
        <span class="lbl">Highlight</span><select id="tlHighlight" class="tlsel">
          <option value="">everything</option>
          <option value="owned">Owned only</option>
          <option value="negative">Undesirable only</option>
          <option value="notowned">Not Owned only</option>
        </select>
        <span class="axislegend" id="axisLegend"></span>
        <span class="caption" id="tlStatus" style="margin:0"></span>
      </div>
      <div class="imp-canvas" id="tlGrid"></div>
      <div class="caption" id="tlCaption"></div>
    </div>

    <div class="cardrow cols-2">
        <div class="card">
          <h3>Key domains in search</h3>
          <div class="sub">who occupies the client's branded search — each domain's share of every tracked result across all searches, cities and polling days in the window</div>
          <div style="overflow-x:auto"><table class="tbl"><thead><tr><th>Domain</th><th title="the asset map's control tier for this domain — see the vocabulary appendix">In the asset map</th><th class="num" style="text-align:right" title="this domain's share of all tracked branded results over the window (every search × city × polling day)">Share</th><th class="num" style="text-align:right" title="the average Google position of the domain's best-performing URL over the window">Avg. pos.</th></tr></thead>
          <tbody id="keyDomRows"></tbody></table></div>
          <div class="caption">Two lenses on ownership: this column is the asset map's control tier for the whole domain; the Owned / Not Owned tags on the captured page are IMPACT's per-result measurement. They can differ — the client's app-store listing is a mapped, client-controlled asset, but IMPACT tags the result Not Owned because the ranking domain belongs to Google.</div>
        </div>
        <div class="card">
          <h3>Has this moved?</h3>
          <div class="sub">the movement story for one search — pick the term, city and window to analyse</div>
          <div class="mvctl">
            <select id="mvTerm" class="tlsel" title="search term"></select>
            <select id="mvLoc" class="tlsel" title="city"></select>
            <input type="date" id="mvStart" class="tlsel" title="window start">
            <span class="sep">→</span>
            <input type="date" id="mvEnd" class="tlsel" title="window end">
          </div>
          <div class="sub" id="trendMeta"></div>
          <div id="trendChart"></div>
          <ul class="findlist sm" id="trendTakeaways"></ul>
          <div id="moversList"></div>
        </div>
        <div class="card" id="smCard" style="display:none">
          <h3>Page-1 ownership across your searches</h3>
          <div class="sub">% of page-1 slots you own on each tracked term &amp; market — one mini-trend per search, all on the same 0–100% scale</div>
          <div class="smterms" id="smTerms"></div>
          <div class="smgrid" id="smGrid"></div>
          <div id="smTable"></div>
        </div>
    </div>`, el);

  wireImpControls(rows, state);
  refreshImpCanvas(rows, state);
  renderKeyDomains(state);
  renderTrend(state);
  renderSmallMultiples(state);
}
